import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Checkbox,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
} from "@mui/material";
import moment from "moment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import styles from "../../JobBatch.module.css";
import PdfIcon from "../../../../../shared/assets/icons/PdfIcon.png";
import ModalToUpdateBatches from "../../ModalToUpdateBatches";
import ModalToOpenEditJobs from "../../ModalToOpenEditJobs";
import axios from "axios";
import RecceReport from "../../../../RFPDashboard/RecceApproval/RecceReport";
import StoreSearch from "../../../Campaign/StoreSearch.js";

const AllBatch = ({
  setAvailableBatches,
  availableBatches,
  availableJobs,
  fetchAllJobsBasedOnCampaignId,
  fetchAllBatchesBasedOnCampaignId,
  isAllBatchesLoading,
  selectedBatchIds,
  setSelectedBatchIds,
  setAcceptedBatches,
  setIsNotAccepted,
  updateJobInParent,
  campaignData,
}) => {
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [openModalToUpdateBatch, setOpenModalToUpdateBatch] = useState(false);
  const [detailsOfBatchSelected, setDetailsOfBatchSelected] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openRecceReport, setOpenRecceReport] = useState(false);
  const [currentRecceJob, setCurrentRecceJob] = useState(null);
  const batchRefs = useRef({});
  const containerRef = useRef(null);

  // Check if Recce service is needed in this campaign
  const isRecceNeeded =
    campaignData?.scopeOfWork?.servicesNeeded?.includes("Recce");

  const handleSelectBatch = (batchId) => {
    setSelectedBatchIds((prevSelected) =>
      prevSelected.includes(batchId)
        ? prevSelected.filter((id) => id !== batchId)
        : [...prevSelected, batchId],
    );
  };

  const handleSelectAll = (event) => {
    const unpublishedBatches = availableBatches.filter(
      (batch) => !batch.isPublished,
    );
    if (event.target.checked) {
      setSelectedBatchIds(unpublishedBatches.map((batch) => batch._id));
    } else {
      setSelectedBatchIds([]);
    }
  };

  const numSelected = selectedBatchIds.length;
  const numUnpublished = availableBatches.filter(
    (batch) => !batch.isPublished,
  ).length;

  const handleAccordionToggle = (batchTitle) => (event) => {
    event.stopPropagation();
    setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
  };

  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditJob = () => {
    setOpenModal(true);
    handleCloseMenu();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Function to handle opening the recce report
  const handleOpenRecceReport = (job) => {
    setCurrentRecceJob(job);
    setOpenRecceReport(true);
  };

  // Function to handle closing the recce report
  const handleCloseRecceReport = () => {
    setOpenRecceReport(false);
  };

  const updateJob = async (job) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/updateJob/${job._id}`,
        job,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating job:", error);
      return null;
    }
  };

  const handleSaveJob = async (updatedFields) => {
    if (!selectedJob || !selectedJob._id) {
      console.error("Selected job does not have an id");
      return;
    }

    const updatedJob = await updateJob(updatedFields);
    if (updatedJob) {
      updateJobInParent(updatedJob);

      const updatedBatches = availableBatches.map((batch) => ({
        ...batch,
        jobs: batch.jobs.map((job) =>
          job._id === updatedJob._id ? updatedJob : job,
        ),
      }));
      setAvailableBatches(updatedBatches);

      handleCloseModal();
    }
  };

  useEffect(() => {
    if (availableJobs && availableBatches.length > 0) {
      // Map and initialize batches
      const updatedBatches = availableBatches.map((batch) => ({
        ...batch,
        // jobs: [], // Initialize jobs array for each batch
      }));

      // Group jobs under their respective batches
      availableJobs.forEach((job) => {
        const batch = updatedBatches.find(
          (batch) => batch._id === job.batchOfJobsForInstallationPartnerId,
        );
        if (batch) {
          batch.jobs.push(job);
        }
      });

      // Calculate dateRange and total payment for each batch
      const batchesWithDateRangesAndPayments = updatedBatches.map((batch) => {
        let totalPayment = 0;
        let jobDates = [];

        if (batch.jobs && batch.jobs.length > 0) {
          // Calculate total payment by summing all job payments
          totalPayment = batch.jobs.reduce(
            (sum, job) => sum + (job.paymentAmount || 0),
            0,
          );

          // Get all job dates
          jobDates = batch.jobs
            .filter((job) => job.jobStartingDate && job.jobCompletionDate)
            .map((job) => ({
              start: new Date(job.jobStartingDate),
              end: new Date(job.jobCompletionDate),
            }));
        }

        // Format date range if we have dates
        let formattedDateRange = "No Dates Available";
        if (jobDates.length > 0) {
          const earliestStartDate = new Date(
            Math.min(...jobDates.map((date) => date.start)),
          );
          const latestEndDate = new Date(
            Math.max(...jobDates.map((date) => date.end)),
          );
          formattedDateRange = `${earliestStartDate.toLocaleDateString()} - ${latestEndDate.toLocaleDateString()}`;
        }

        return {
          ...batch,
          dateRange: formattedDateRange,
          totalPayment,
        };
      });

      // Update batches only if there is a change
      setAvailableBatches((prevBatches) => {
        const prevBatchesString = JSON.stringify(prevBatches);
        const updatedBatchesString = JSON.stringify(
          batchesWithDateRangesAndPayments,
        );

        if (prevBatchesString !== updatedBatchesString) {
          return batchesWithDateRangesAndPayments;
        }
        return prevBatches;
      });
    }
  }, [availableJobs, availableBatches]);

  const searchData = useMemo(() => {
    const result = [];

    availableBatches.forEach((batch) => {
      batch.jobs?.forEach((job) => {
        result.push({
          id: job._id,
          storeName: job.storeName,
          batchTitle: batch.batchTitle,
          targetId: batch._id,
          subText: `Batch: ${batch.batchTitle}`,
        });
      });
    });

    return result;
  }, [availableBatches]);

  return (
    <>
      <Grid ref={containerRef} className={`${styles.scrollableContainer}`}>
        <div className="mx-3 py-2 sticky top-0 z-20 bg-white">
          <StoreSearch
            data={searchData}
            searchKey="storeName"
            placeholder="Search Store Name"
            containerRef={containerRef}
            targetRefs={batchRefs}
            onSelect={(item) => {
              setExpandedBatch(item.batchTitle);
            }}
          />
        </div>
        {isAllBatchesLoading ? (
          <div className={styles.noJobsStyle}>
            <CircularProgress />
          </div>
        ) : availableBatches?.length > 0 ? (
          <>
            {numUnpublished > 0 && (
              <Box display="flex" alignItems="center" mb={2} ml={2}>
                <Checkbox
                  indeterminate={
                    numSelected > 0 && numSelected < numUnpublished
                  }
                  checked={numUnpublished > 0 && numSelected === numUnpublished}
                  onChange={handleSelectAll}
                  color="primary"
                />
                <Typography variant="body2">
                  {numSelected} of {numUnpublished} unpublished batches selected
                </Typography>
              </Box>
            )}

            {availableBatches.map((batch, index) => (
              <Grid
                ref={(el) => (batchRefs.current[batch._id] = el)}
                item
                xs={12}
                md={12}
                key={index}
                my={1}
                mx={2}
              >
                <Accordion expanded={expandedBatch === batch.batchTitle}>
                  <AccordionSummary
                    expandIcon={
                      <IconButton
                        onClick={handleAccordionToggle(batch.batchTitle)}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    }
                    aria-controls={`panel-${index}-content`}
                    id={`panel-${index}-header`}
                  >
                    <div className="flex items-center justify-between p-4 rounded-lg bg-white w-full mx-auto">
                      <div className="flex items-center">
                        {!batch.isPublished ? (
                          <Checkbox
                            checked={selectedBatchIds.includes(batch._id)}
                            onChange={() => handleSelectBatch(batch._id)}
                            color="primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div style={{ width: "42px" }} />
                        )}
                        <h1 className={styles.batchTitle}>
                          {batch.batchTitle}
                        </h1>
                      </div>

                      <div className={styles.chipContainer}>
                        <Chip
                          label={
                            batch.isPublished ? "Published" : "Not Published"
                          }
                          sx={{
                            backgroundColor: batch.isPublished
                              ? "#D0F4FF"
                              : "#FFDFDF",
                            color: batch.isPublished ? "#093EBC" : "#8C0909",
                          }}
                        />

                        <div>
                          <Chip
                            label={`Timeline: ${moment(
                              batch?.jobStartingDate,
                            ).format("DD MMM YY")} - ${moment(
                              batch?.jobCompletionDate,
                            ).format("DD MMM YY")}`}
                            className={styles.chip}
                          />
                        </div>

                        <Chip
                          label={
                            <span>Batch Payment: ₹{batch?.totalPayment}</span>
                          }
                          className={styles.chips}
                        />
                        {/* User Info */}
                        {/* <Chip label={batch.contact} className={styles.chip} /> */}

                        {/* Status Badge */}
                        {/* <Chip label={batch.status} className={styles.pending} /> */}

                        {/* Ellipsis Button */}
                        <button
                          className="text-gray-400 hover:text-gray-600 text-xl rounded-full px-4 py-1 hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenModalToUpdateBatch(true);
                            setDetailsOfBatchSelected(batch);
                          }}
                        >
                          ⋮
                        </button>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className={styles.accordionDetails}>
                    <Box className={`${styles.scrollableBox}`}>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow className={styles.tableHeader}>
                              <TableCell className={styles.tableCell}>
                                ADDRESS
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                STORE MANAGER
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                PRODUCT TYPE
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                DRIVER DETAILS
                              </TableCell>
                              {/* <TableCell className={styles.tableCell}>
                                  JOB DATE
                                </TableCell> */}
                              <TableCell className={styles.tableCell}>
                                JOB PAY
                              </TableCell>
                              {/* <TableCell className={styles.tableCell}>
                                  KAM APPROVAL
                                </TableCell>
                                <TableCell className={styles.tableCell}>
                                  CLIENT APPROVAL
                                </TableCell> */}

                              {/* Conditionally render RECCE REPORT column */}
                              {isRecceNeeded && (
                                <TableCell className={styles.tableCell}>
                                  RECCE REPORT
                                </TableCell>
                              )}
                              {/* <TableCell className={styles.tableCell}>
                                  JOB STATUS
                                </TableCell> */}
                              {/* <TableCell className={styles.tableCell}>
                                  IP PAY
                                </TableCell> */}

                              <TableCell className={styles.tableCell}>
                                ACTION
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {batch.jobs?.length > 0 ? (
                              batch.jobs.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row?.storeName}
                                    </div>
                                    <div className={styles.address2}>
                                      {row.address}
                                    </div>
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row?.storeOwnerName}
                                    </div>
                                    <div className={styles.storeManagerContact}>
                                      {row?.storeOwnerPhoneNumber}
                                    </div>
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row?.productId?.productType || "NA"}
                                    </div>
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row.driverDetails?.driverName || "NA"}
                                    </div>
                                    <div className={styles.address2}>
                                      {row.driverDetails?.driverPhoneNumber ||
                                        "NA"}{" "}
                                      |
                                      {row.driverDetails?.vehicleNumber || "NA"}
                                    </div>
                                  </TableCell>
                                  {/* <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row.jobDate}
                                    </div>
                                  </TableCell> */}
                                  <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      ₹{row.paymentAmount}
                                    </div>
                                  </TableCell>
                                  {/* <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row?.kamApproval || "NA"}
                                    </div>
                                  </TableCell> */}

                                  {/* <TableCell className={styles.tableCell}>
                                    <div className={styles.sharedTextStyle}>
                                      {row?.clientApproval || "NA"}
                                    </div>
                                  </TableCell> */}

                                  {/* Conditionally render recce report cell */}
                                  {isRecceNeeded && (
                                    <TableCell className={styles.tableCell}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          handleOpenRecceReport(row)
                                        }
                                      >
                                        <img
                                          src={PdfIcon}
                                          alt="PDF Icon"
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            marginRight: "8px",
                                          }}
                                        />
                                        <a
                                          href="#"
                                          style={{
                                            cursor: "pointer",
                                            textDecoration: "none",
                                            color: "inherit",
                                          }}
                                        >
                                          {row.RecceReport}
                                        </a>
                                      </div>
                                    </TableCell>
                                  )}
                                  <TableCell className={styles.tableCell}>
                                    {row.status != 2 && (
                                      <IconButton
                                        onClick={(event) =>
                                          handleMenuClick(event, row)
                                        }
                                      >
                                        <MoreVertIcon />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={isRecceNeeded ? 6 : 5}
                                  align="center"
                                >
                                  No jobs found in this batch
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </AccordionDetails>
                  {/* <BatchActions /> */}
                </Accordion>
              </Grid>
            ))}
          </>
        ) : (
          <div className={styles.noJobsStyle}>
            <Typography
              variant="h6"
              align="center"
              className={`${styles.noJobsBox} ${styles.noJobsText}`}
              style={{ margin: "2rem 0" }}
            >
              No batches available
            </Typography>
          </div>
        )}
      </Grid>

      {openModalToUpdateBatch && (
        <ModalToUpdateBatches
          openModal={openModalToUpdateBatch}
          setOpenModalToUpdateBatch={setOpenModalToUpdateBatch}
          batch={detailsOfBatchSelected}
          fetchAllJobsBasedOnCampaignId={fetchAllJobsBasedOnCampaignId}
          fetchAllBatchesBasedOnCampaignId={fetchAllBatchesBasedOnCampaignId}
          setAvailableBatches={setAvailableBatches}
          setAcceptedBatches={setAcceptedBatches}
          setIsNotAccepted={setIsNotAccepted}
          onBatchUpdate={(updatedBatch) => {
            setAvailableBatches((prev) =>
              prev.map((b) => (b._id === updatedBatch._id ? updatedBatch : b)),
            );
            if (detailsOfBatchSelected?._id === updatedBatch._id) {
              setDetailsOfBatchSelected(updatedBatch);
            }
          }}
          campaignData={campaignData}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditJob}>Edit Job</MenuItem>
      </Menu>

      <ModalToOpenEditJobs
        open={openModal}
        handleClose={handleCloseModal}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        handleSaveJob={handleSaveJob}
      />

      {/* Recce Report Dialog */}
      <Dialog
        open={openRecceReport}
        onClose={handleCloseRecceReport}
        maxWidth="lg"
        fullWidth
        className="custom-dialog"
        PaperProps={{
          className: "custom-dialog-paper",
        }}
      >
        <DialogContent>
          {currentRecceJob && (
            <RecceReport
              setOpen={setOpenRecceReport}
              open={openRecceReport}
              currentApproval={currentRecceJob}
              finalSpaces={[]}
              installationCheck={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllBatch;
